import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ScrollView, Platform } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import LocationFilterDropdown, { FilterOption } from './LocationFilterDropdown';

export interface LocationFilterPanelProps {
  visible: boolean;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;

  // Filter state and handlers
  regionOptions: FilterOption[];
  marketOptions: FilterOption[];
  locationOptions: FilterOption[];
  departmentOptions: FilterOption[];
  orgOptions: FilterOption[];
  locationTypeOptions: FilterOption[];

  selectedRegions: FilterOption[];
  onRegionChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedMarkets: FilterOption[];
  onMarketChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedDepartments: FilterOption[];
  onDepartmentChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedOrgs: FilterOption[];
  onOrgChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedTpl: FilterOption[];
  onTplChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedUnionStatuses: FilterOption[];
  onUnionStatusChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedSizes: FilterOption[];
  onSizeChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedLocations: FilterOption[];
  onLocationChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedLocationTypes: FilterOption[];
  onLocationTypeChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedStatuses: string[];
  onStatusChange: (value: string[]) => void;

  isLocationDisabled?: boolean;
  locationPlaceholder?: string;
  isLoadingRegions?: boolean;
  isLoadingMarkets?: boolean;
  isLoadingLocations?: boolean;
}

const LocationFilterPanel: React.FC<LocationFilterPanelProps> = ({
  visible,
  onClose,
  onApply,
  onReset,
  regionOptions,
  marketOptions,
  locationOptions,
  departmentOptions,
  orgOptions,
  locationTypeOptions,
  selectedRegions,
  onRegionChange,
  selectedMarkets,
  onMarketChange,
  selectedDepartments,
  onDepartmentChange,
  selectedOrgs,
  onOrgChange,
  selectedTpl,
  onTplChange,
  selectedUnionStatuses,
  onUnionStatusChange,
  selectedSizes,
  onSizeChange,
  selectedLocations,
  onLocationChange,
  selectedLocationTypes,
  onLocationTypeChange,
  selectedStatuses,
  onStatusChange,
  isLocationDisabled = false,
  locationPlaceholder = 'Select...',
  isLoadingRegions = false,
  isLoadingMarkets = false,
  isLoadingLocations = false,
}) => {
  const statusOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Active', displayText: 'Active' },
      { id: 'Pending Activation', displayText: 'Pending Activation' },
      { id: 'Marked for Removal', displayText: 'Marked for Removal' },
      { id: 'Disabled', displayText: 'Disabled' },
    ],
    []
  );

  const selectedStatusObjects = useMemo(
    () => selectedStatuses
      .map(s => statusOptions.find(opt => opt.id === s))
      .filter((s): s is FilterOption => !!s),
    [selectedStatuses, statusOptions]
  );

  const tplOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Yes', displayText: 'Yes' },
      { id: 'No', displayText: 'No' },
    ],
    []
  );

  const unionStatusOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Union', displayText: 'Union' },
      { id: 'Non-Union', displayText: 'Non-Union' },
      { id: 'Unknown', displayText: 'Unknown' },
    ],
    []
  );

  const sizeOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Small', displayText: 'Small' },
      { id: 'Medium', displayText: 'Medium' },
      { id: 'Large', displayText: 'Large' },
    ],
    []
  );

  const selectedTplObjects = useMemo(
    () => selectedTpl
      .map(s => tplOptions.find(opt => opt.id === s.id))
      .filter((s): s is FilterOption => !!s),
    [selectedTpl, tplOptions]
  );

  const selectedUnionStatusObjects = useMemo(
    () => selectedUnionStatuses
      .map(s => unionStatusOptions.find(opt => opt.id === s.id))
      .filter((s): s is FilterOption => !!s),
    [selectedUnionStatuses, unionStatusOptions]
  );

  const selectedSizeObjects = useMemo(
    () => selectedSizes
      .map(s => sizeOptions.find(opt => opt.id === s.id))
      .filter((s): s is FilterOption => !!s),
    [selectedSizes, sizeOptions]
  );

  const handleStatusChange = (value: FilterOption | FilterOption[] | null) => {
    if (Array.isArray(value)) {
      onStatusChange(value.map(v => String(v.id)));
    } else if (value) {
      onStatusChange([String(value.id)]);
    } else {
      onStatusChange([]);
    }
  };

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    if (selectedRegions.length > 0) count++;
    if (selectedMarkets.length > 0) count++;
    if (selectedDepartments.length > 0) count++;
    if (selectedOrgs.length > 0) count++;
    if (selectedTpl.length > 0) count++;
    if (selectedUnionStatuses.length > 0) count++;
    if (selectedSizes.length > 0) count++;
    if (selectedLocations.length > 0) count++;
    if (selectedLocationTypes.length > 0) count++;
    if (selectedStatuses.length > 0) count++;
    return count;
  }, [selectedRegions, selectedMarkets, selectedDepartments, selectedOrgs, selectedTpl, selectedUnionStatuses, selectedSizes, selectedLocations, selectedLocationTypes, selectedStatuses]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType={Platform.OS === 'web' ? 'none' : 'slide'}
      onRequestClose={onClose}
    >
      {/* Backdrop */}
      <TouchableOpacity
        style={styles.backdrop}
        onPress={onClose}
        activeOpacity={1}
      />

      {/* Panel */}
      <View style={styles.panel}>
        {/* Header */}
        <View style={styles.panelHeader}>
          <Text style={styles.panelTitle}>Filters</Text>
          {appliedFilterCount > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>{appliedFilterCount}</Text>
            </View>
          )}
          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <MaterialIcons name="close" size={20} color={AdminColors.textPrimary} />
          </TouchableOpacity>
        </View>

        {/* Content */}
        <ScrollView style={styles.panelContent} contentContainerStyle={styles.contentContainer}>
          {/* Region */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Region</Text>
            <LocationFilterDropdown
              label="Region"
              options={regionOptions}
              value={selectedRegions.length > 0 ? selectedRegions : null}
              onChange={onRegionChange}
              isMulti
              isLoading={isLoadingRegions}
            />
          </View>

          {/* Market */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Market</Text>
            <LocationFilterDropdown
              label="Market"
              options={marketOptions}
              value={selectedMarkets.length > 0 ? selectedMarkets : null}
              onChange={onMarketChange}
              isMulti
              isLoading={isLoadingMarkets}
            />
          </View>

          {/* Location */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Location</Text>
            <LocationFilterDropdown
              label="Location"
              options={locationOptions}
              value={selectedLocations.length > 0 ? selectedLocations : null}
              onChange={onLocationChange}
              isMulti
              isDisabled={isLocationDisabled}
              isLoading={isLoadingLocations}
              placeholder={locationPlaceholder}
            />
          </View>

          {/* Location Type */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Location Type</Text>
            <LocationFilterDropdown
              label="Location Type"
              options={locationTypeOptions}
              value={selectedLocationTypes.length > 0 ? selectedLocationTypes : null}
              onChange={onLocationTypeChange}
              isMulti
            />
          </View>

          {/* Union Status */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Union Status</Text>
            <LocationFilterDropdown
              label="Union Status"
              options={unionStatusOptions}
              value={selectedUnionStatusObjects.length > 0 ? selectedUnionStatusObjects : null}
              onChange={onUnionStatusChange}
              isMulti
            />
          </View>

          {/* Department */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Department</Text>
            <LocationFilterDropdown
              label="Department"
              options={departmentOptions}
              value={selectedDepartments.length > 0 ? selectedDepartments : null}
              onChange={onDepartmentChange}
              isMulti
            />
          </View>

          {/* Org */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Org</Text>
            <LocationFilterDropdown
              label="Org"
              options={orgOptions}
              value={selectedOrgs.length > 0 ? selectedOrgs : null}
              onChange={onOrgChange}
              isMulti
            />
          </View>

          {/* 3PL */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>3PL</Text>
            <LocationFilterDropdown
              label="3PL"
              options={tplOptions}
              value={selectedTplObjects.length > 0 ? selectedTplObjects : null}
              onChange={onTplChange}
              isMulti
            />
          </View>

          {/* Size */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Size</Text>
            <LocationFilterDropdown
              label="Size"
              options={sizeOptions}
              value={selectedSizeObjects.length > 0 ? selectedSizeObjects : null}
              onChange={onSizeChange}
              isMulti
            />
          </View>

          {/* Status */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterGroupLabel}>Status</Text>
            <LocationFilterDropdown
              label="Status"
              options={statusOptions}
              value={selectedStatusObjects.length > 0 ? selectedStatusObjects : null}
              onChange={handleStatusChange}
              isMulti
            />
          </View>
        </ScrollView>

        {/* Footer with Action Buttons */}
        <View style={styles.panelFooter}>
          <TouchableOpacity
            style={[styles.actionBtn, styles.resetBtn]}
            onPress={onReset}
            activeOpacity={0.7}
          >
            <Text style={styles.resetBtnText}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.actionBtn, styles.applyBtn]}
            onPress={onApply}
            activeOpacity={0.7}
          >
            <Text style={styles.applyBtnText}>Apply</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  panel: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: AdminColors.bgSurface,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    maxHeight: '85%',
    flexDirection: 'column',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 10,
  },
  panelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
    flex: 1,
  },
  filterBadge: {
    backgroundColor: AdminColors.blue700,
    borderRadius: 10,
    minWidth: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  filterBadgeText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  panelContent: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    gap: 16,
  },
  filterGroup: {
    gap: 8,
  },
  filterGroupLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },
  panelFooter: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: AdminColors.gray25,
  },
  actionBtn: {
    flex: 1,
    minHeight: 40,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resetBtn: {
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  resetBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },
  applyBtn: {
    backgroundColor: AdminColors.blue700,
  },
  applyBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    fontFamily: FontFamily.medium,
  },
});

export default LocationFilterPanel;
